import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import Icon from '../../Icon';

function AdminAddDepositView({ user }) {
  const navigate = useNavigate();
  const [depositTarget, setDepositTarget] = useState('tenant'); // 'tenant' | 'admin'
  const [tenants, setTenants] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [transId, setTransId] = useState('');
  const [transAmount, setTransAmount] = useState('');
  const [billRefNumber, setBillRefNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const res = await api.get('/auth/users');
        const allUsers = Array.isArray(res?.data?.users) ? res.data.users : [];
        const tenantUsers = allUsers.filter((u) => {
          if (u?.role === 'T') return true;
          return Array.isArray(u?.propertyRoles) && u.propertyRoles.some((pr) => pr?.role === 'T');
        });
        setTenants(tenantUsers);
      } catch (error) {
        console.error('Failed to fetch tenants', error);
        showNotification('error', 'Failed to load tenants');
      }
    };
    fetchTenants();
  }, []);

  const filteredTenants = useMemo(() => {
    if (!searchQuery.trim()) return tenants;
    const q = searchQuery.toLowerCase();
    return tenants.filter((t) => {
      return (
        (t?.name || '').toLowerCase().includes(q) ||
        (t?.email || '').toLowerCase().includes(q) ||
        (t?.phone || '').toLowerCase().includes(q) ||
        (t?.idNumber || '').toLowerCase().includes(q)
      );
    });
  }, [searchQuery, tenants]);

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleSelectTenant = (tenant) => {
    setSelectedTenant(tenant);
    setBillRefNumber(tenant?.idNumber || '');
    setDropdownOpen(false);
    setSearchQuery('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (depositTarget === 'tenant' && !selectedTenant?._id) {
      showNotification('error', 'Please select a tenant');
      return;
    }
    if (!transId.trim()) {
      showNotification('error', 'TransID is required');
      return;
    }
    if (!transAmount || Number(transAmount) <= 0) {
      showNotification('error', 'TransAmount must be greater than 0');
      return;
    }
    if (depositTarget === 'tenant' && !billRefNumber.trim()) {
      showNotification('error', 'BillRefNumber is required');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        TransID: transId.trim(),
        TransAmount: transAmount,
        BillRefNumber: billRefNumber.trim() || undefined
      };
      if (depositTarget === 'admin') {
        payload.target = 'admin';
      } else {
        payload.userId = selectedTenant._id;
      }
      const res = await api.post('/wallet/admin/add-deposit', payload);
      showNotification('success', res?.data?.message || 'Deposit recorded');
      setTransId('');
      setTransAmount('');
    } catch (error) {
      console.error('Failed to add deposit', error);
      showNotification('error', error?.response?.data?.error || 'Failed to add deposit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="font-sans">
      {notification && (
        <div className="fixed top-6 right-6 z-50">
          <div className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border ${notification.type === 'success'
              ? 'bg-emerald-600 border-emerald-500 text-white'
              : 'bg-rose-600 border-rose-500 text-white'
            }`}>
            <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
              {notification.type === 'success' ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            <p className="text-sm font-medium">{notification.message}</p>
            <button
              onClick={() => setNotification(null)}
              className="ml-2 w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="p-4 sm:p-6">
        {/* Header Section */}
        <div className="mb-6">
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-xl sm:rounded-2xl p-6 sm:p-8 text-white shadow-lg border border-emerald-600">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/10 rounded-full flex items-center justify-center border-2 border-white/20">
                  <Icon name="wallet" size="1.5rem" className="text-white" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-white">
                    Add Deposit
                  </h1>
                  <p className="text-white/80 text-sm sm:text-base mt-1">
                    {depositTarget === 'admin'
                      ? 'Record a manual deposit into the Admin wallet'
                      : 'Record a manual wallet deposit for a tenant'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/admin')}
                className="group flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-white transition-colors"
              >
                <svg className="w-4 h-4 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="text-sm font-medium hidden sm:inline">Dashboard</span>
              </button>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-200">
            <form className="p-6 sm:p-8 space-y-6" onSubmit={handleSubmit}>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setDepositTarget('tenant')}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                    depositTarget === 'tenant'
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-500 hover:text-emerald-700'
                  }`}
                >
                  Tenant Wallet
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDepositTarget('admin');
                    setSelectedTenant(null);
                    setDropdownOpen(false);
                    setSearchQuery('');
                    setBillRefNumber('');
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                    depositTarget === 'admin'
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-500 hover:text-emerald-700'
                  }`}
                >
                  Admin Wallet
                </button>
              </div>

              <div className="space-y-3">
                <label className="text-sm font-semibold text-gray-700">{depositTarget === 'admin' ? 'Wallet' : 'Tenant'}</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setDropdownOpen((prev) => !prev)}
                    disabled={depositTarget === 'admin'}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-white text-gray-900 border border-gray-300 hover:border-emerald-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <span className="text-left">
                      {depositTarget === 'admin'
                        ? 'Admin Wallet'
                        : (selectedTenant ? `${selectedTenant.name} - ${selectedTenant.idNumber}` : 'Select a tenant')}
                    </span>
                    <Icon name={dropdownOpen ? 'chevron-up' : 'chevron-down'} size="1rem" />
                  </button>
                  {depositTarget !== 'admin' && dropdownOpen && (
                    <div className="absolute z-20 mt-2 w-full rounded-lg bg-white text-gray-800 shadow-xl border border-gray-200 overflow-hidden">
                      <div className="p-3 border-b border-gray-200">
                        <input
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search name, email, phone, ID"
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {filteredTenants.length === 0 && (
                          <div className="px-4 py-3 text-sm text-gray-500">No tenants found</div>
                        )}
                        {filteredTenants.map((tenant) => (
                          <button
                            type="button"
                            key={tenant._id}
                            onClick={() => handleSelectTenant(tenant)}
                            className="w-full text-left px-4 py-3 hover:bg-emerald-50 transition-colors"
                          >
                            <div className="font-medium text-gray-800">{tenant.name}</div>
                            <div className="text-xs text-gray-500">{tenant.email || tenant.phone || tenant.idNumber}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">TransID</label>
                  <input
                    value={transId}
                    onChange={(e) => setTransId(e.target.value)}
                    placeholder="MPesa transaction ID"
                    className="w-full px-4 py-3 rounded-lg bg-white text-gray-900 placeholder-gray-400 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">TransAmount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={transAmount}
                    onChange={(e) => setTransAmount(e.target.value)}
                    placeholder="Amount"
                    className="w-full px-4 py-3 rounded-lg bg-white text-gray-900 placeholder-gray-400 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">
                  {depositTarget === 'admin' ? 'Reference (optional)' : 'BillRefNumber'}
                </label>
                <input
                  value={billRefNumber}
                  onChange={(e) => setBillRefNumber(e.target.value)}
                  placeholder={depositTarget === 'admin' ? 'Optional reference' : 'Tenant ID number'}
                  className="w-full px-4 py-3 rounded-lg bg-white text-gray-900 placeholder-gray-400 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
                <p className="text-xs text-gray-500">
                  {depositTarget === 'admin'
                    ? 'Optional reference for reconciliation.'
                    : 'BillRefNumber should match the selected tenant ID number.'}
                </p>
              </div>

              <div className="flex items-center justify-between gap-4 pt-4 border-t border-gray-200">
                <div className="text-xs text-gray-500">
                  Logged in as {user?.name || 'Admin'}
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-3 rounded-lg bg-emerald-600 text-white font-semibold shadow-md hover:bg-emerald-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Submitting...' : 'Add Deposit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminAddDepositView;
